import axios from "axios";
import { toast } from "react-toastify";
import { API_BASE_PATH } from "../../environment";

const PERMISSION_BASE_URL = `${API_BASE_PATH}/permission`;

// ✅ POST payload
export interface PermissionRequest {
  duration_hours: number;
  start_time: string; // HH:mm:ss
  end_time: string; // HH:mm:ss
  date_apply: string; // YYYY-MM-DD
  reason: string;
}

// 📦 PUT payload
export interface PermissionUpdate {
  permission_id: number;
  status: "Approved" | "Rejected";
  reject_reason?: string;
  notify: string;
}

// 📥 Response record
export interface PermissionRecord {
  permission_id: number;
  employee_id: string;
  date_apply: string;
  duration_hours: number;
  start_time: string;
  end_time: string;
  reason: string;
  reject_reason: string | null;
  status: "Pending" | "Approved" | "Rejected";
  approved_by: string | null;
  approved_date: string | null;
  created_at: string;
  updated_at: string | null;
  notify: string;
}

// 📊 Widgets response
export interface PermissionWidgets {
  applied: number;
  approved: number;
  rejected: number;
  pending: number;
}

// ====== Error Extractor ======
const extractErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, ""); // strip numeric codes
  }

  return detail || details || message ;
};

// 🌟 POST - Create a permission request
export const postPermission = async (
  data: PermissionRequest,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, value.toString());
    });

    const response = await axios.post(
      `${PERMISSION_BASE_URL}/`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Permission submitted successfully");
    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 🔍 GET - Self permission requests (Employee)
export const getSelfPermissions = async (
  token: string | null
): Promise<PermissionRecord[]> => {
  try {
    const response = await axios.get(`${PERMISSION_BASE_URL}/self/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 🔍 GET - All permission requests (Admin / Manager)
export const getAllPermissions = async (
  token: string | null
): Promise<PermissionRecord[]> => {
  try {
    const response = await axios.get(`${PERMISSION_BASE_URL}/all/`, {
      headers: {
        accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// 📊 GET - Permission Widgets (Monthly Summary)
export const getPermissionWidgets = async (
  month: number,
  year: number,
  day?: number | null,
  employee_id?: string | null,
  token?: string | null
): Promise<PermissionWidgets> => {
  try {
    const params = new URLSearchParams();
    params.append("month", month.toString());
    params.append("year", year.toString());
    if (day) params.append("day", day.toString());
    if (employee_id) params.append("employee_id", employee_id);

    const response = await axios.get(
      `${PERMISSION_BASE_URL}/widgets?${params.toString()}`,
      {
        headers: {
          accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      }
    );

    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};

// ✅ PUT - Approve / Reject permission request (Admin / Manager)
export const updatePermissionStatus = async (
  data: PermissionUpdate,
  token: string | null
): Promise<any> => {
  try {
    const formData = new URLSearchParams();
    formData.append("status", data.status);
    if (data.reject_reason) {
      formData.append("reject_reason", data.reject_reason);
    }

    const response = await axios.put(
      `${PERMISSION_BASE_URL}/?permission_id=${data.permission_id}`,
      formData.toString(),
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success(`Permission ${data.status}`);
    return response.data;
  } catch (error: any) {
    toast.error(extractErrorMessage(error));
    throw error;
  }
};
